import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { toAbsoluteUrl } from "../modules/utils/AssetHelpers";
import Icon from "../modules/components/Icon";
import { Dialog } from "primereact/dialog";
import { BillExampleModal } from "../modules/components/home/BillExampleModal";
import { login } from "../modules/api/auth";
import Swal from "sweetalert2";

// เลขทะเบียนผู้ใช้น้ำ: ตัดช่องว่างหัวท้าย และแปลงเป็นตัวพิมพ์ใหญ่
const normalizeRegistrationNumber = (value: string) => (value || "").trim().toUpperCase();

// เบอร์โทรศัพท์: เอาเฉพาะตัวเลข
const normalizePhone = (value: string) => (value || "").replace(/\D/g, "");

const HomeForm = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [visible, setVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      const response = await login({
        registrationNumber: normalizeRegistrationNumber(data.registrationNumber),
        phone: normalizePhone(data.phone),
        deviceName: 'web-client'
      });

      // ตรวจสอบ responseStatus จาก API
      if (response?.responseStatus === "false" || response?.responseStatus === false) {
        // กรณีไม่พบข้อมูลผู้ใช้น้ำ
        setIsLoading(false);
        await Swal.fire({
          icon: 'warning',
          title: 'ไม่พบข้อมูลผู้ใช้น้ำ',
          text: response?.responseMessage || 'กรุณาตรวจสอบเลขทะเบียนผู้ใช้น้ำและเบอร์โทรศัพท์',
          confirmButtonText: 'ปิด',
          confirmButtonColor: '#547DE5',
        });
        return;
      }

      // เก็บ token และ customer.id หรือข้อมูลการ login (ถ้ามี)
      if (response?.data?.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
      }
      if (response?.data?.customer?.id) {
        localStorage.setItem('customerId', response.data.customer.id.toString());
      }

      // แสดง modal สำเร็จ
      await Swal.fire({
        icon: 'success',
        title: 'เข้าสู่ระบบสำเร็จ',
        text: 'กำลังนำท่านไปยังหน้าข้อมูลผู้ใช้น้ำ',
        showConfirmButton: false,
        timer: 1500
      });

      // นำทางไปหน้าถัดไป
      navigate('/record-invoice');
    } catch (error: any) {
      console.error('Login failed:', error);
      setIsLoading(false);

      // จัดการ error จาก API
      const errorMessage = error?.response?.data?.responseMessage
        || error?.response?.data?.message
        || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

      await Swal.fire({
        icon: 'error',
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        text: errorMessage,
        confirmButtonText: 'ลองอีกครั้ง',
        confirmButtonColor: '#547DE5',
      });
    }
  };






  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <div className="bg-white text-6F6B7D" style={{ minHeight: "100vh" }}>
        <div className="row g-0" style={{ minHeight: "100vh" }}>
          {/* Left side - Image */}
          {!isMobile && (
            <div
              className="col-md-6"
              style={{
                backgroundImage: `url('${toAbsoluteUrl("/images/bg.svg")}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
            </div>
          )}

          {/* Right side - Form */}
          <div className={`${isMobile ? "col-12" : "col-md-6"} d-flex justify-content-center align-items-center`}>
            <div className={`${isMobile ? "p-4" : "p-5"}`} style={{ maxWidth: "500px", width: "100%" }}>
              {/* Logo and Title */}
              <div className="d-flex justify-content-center align-items-center mb-5">
                <img
                  src={toAbsoluteUrl("/images/logo.webp")}
                  className="img-fluid rounded-start me-3"
                  alt="logo"
                  style={{
                    width: "500px",
                    height: "auto",
                    objectFit: "cover"
                  }}
                />
              </div>
              {/* Form */}
              <h2 className="mb-2 fs-4">เข้าสู่ระบบ</h2>
              <p className="opacity-50">ตรวจสอบข้อมูลการใช้น้ำ และประวัติการชำระเงินได้ในที่เดียว</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-3">
                  <label htmlFor="registrationNumber" className="form-label">
                    เลขทะเบียนผู้ใช้น้ำ
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="registrationNumber"
                    placeholder="เช่น AR00C-03009"
                    {...register("registrationNumber", {
                      required: "กรุณากรอกเลขทะเบียนผู้ใช้น้ำ",
                      validate: {
                        notBlank: (value: string) =>
                          (value || "").trim() !== "" || "กรุณากรอกเลขทะเบียนผู้ใช้น้ำ",
                        allowedChars: (value: string) =>
                          /^[A-Za-z0-9-]+$/.test((value || "").trim()) ||
                          "เลขทะเบียนผู้ใช้น้ำมีได้เฉพาะตัวอักษรภาษาอังกฤษ ตัวเลข และขีด (-)",
                        length: (value: string) => {
                          // นับความยาวหลังตัดขีดออก
                          const digits = (value || "").trim().replace(/-/g, "");
                          return (
                            (digits.length >= 6 && digits.length <= 12) ||
                            "รูปแบบเลขทะเบียนผู้ใช้น้ำไม่ถูกต้อง"
                          );
                        },
                      },
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="registrationNumber"
                    render={({ message }) => (
                      <p className="text-danger mt-1 mb-0">{message}</p>
                    )}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="phone" className="form-label">
                    เบอร์โทรศัพท์
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone"
                    {...register("phone", {
                      required: "กรุณากรอกเบอร์โทรศัพท์",
                      validate: {
                        notBlank: (value: string) =>
                          (value || "").trim() !== "" || "กรุณากรอกเบอร์โทรศัพท์",
                        format: (value: string) =>
                          // ตัวเลข 9-10 หลัก ขึ้นต้นด้วย 0 (นับหลังตัดอักขระที่ไม่ใช่ตัวเลขออก)
                          /^0\d{8,9}$/.test(normalizePhone(value)) ||
                          "เบอร์โทรศัพท์ไม่ถูกต้อง",
                      },
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="phone"
                    render={({ message }) => (
                      <p className="text-danger mt-1 mb-0">{message}</p>
                    )}
                  />
                </div>

                {/* <div className="mb-3">
                  <label htmlFor="phone" className="form-label">
                    รหัสมาตรวัดน้ำ
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="meter_number"
                    {...register("meter_number", {
                      required: "กรุณากรอกรหัสมาตรวัดน้ำ",
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="meter_number"
                    render={({ message }) => (
                      <p className="text-danger mt-1 mb-0">{message}</p>
                    )}
                  />
                </div> */}

                <div className="mb-3">
                  <button
                    type="submit"
                    className="btn w-100 fs-5 fw-normal d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: isLoading ? "#2d62c6" : "#547DE5",
                      color: "#fff",
                      minHeight: "48px"
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <Icon icon="spinner" className="me-2" spin />
                        <span style={{ fontFamily: 'google sans, sans-serif', color: '#fff' }}>กำลังเข้าสู่ระบบ...</span>
                      </div>
                    ) : (
                      "เข้าสู่ระบบ"
                    )}
                  </button>
                </div>
              </form>

              {/* Description */}
              {/* <div className="mt-4">
                <p className="small">
                  <span className="pointer" onClick={() => setVisible(true)}>
                    {infoIcon}
                  </span>
                  หมายเหตุ : หมายเลขผู้ใช้น้ำ, หมายเลขเขต, รหัสมาตรวัดน้ำ
                  สามารถดูได้จากใบเสร็จที่ออกโดยสำนักการประปาเทศบาลนครนครราชสีมา
                  หรือติดต่อสอบถามข้อมูลได้ที่ สำนักการประปาเทศบาลนครนครราชสีมา
                  โทร 044-234656
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Dialog
        header={
          <div className="d-flex justify-content-start align-items-center border-b p-2">
            ตัวอย่างใบเสร็จ
          </div>
        }
        visible={visible}
        className="my-font"
        style={isMobile ? { width: "90vw" } : { width: "50vw" }}
        onHide={() => setVisible(false)}
      >
        <BillExampleModal />
      </Dialog>
    </>
  );
};

const HomePage = () => {
  return <HomeForm />;
};

export default HomePage;
